import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MAM_PAGE_SIZES, frameTc, rangeLengthMs, type MamAsset, type MamBasketItem, type MamCounts, type MamKind, type MamMedia, type MamSection, type MamSort, type MamSubclip } from '@airpost/shared';
import { ApiError, del, get, post, put } from '../api.js';
import { analyseSound } from '../edit-media.js';
import { t, type Key } from '../i18n.js';
import { stepBasket } from '../mam-basket.js';
import { useSession } from '../session.js';
import { fmtDateInput, fmtDateTime } from '../util.js';
import { Link, navigate } from '../router.js';
import { MamPlayer, type MamPlayerHandle, type PlayerMarks, type PlayerSource } from './mam-player.js';

/**
 * The Video Hub as a media asset manager, laid out like a broadcast MAM: the catalogue's views on the left, the
 * asset list in the middle (a dense list with thumbnails or a grid, columns to choose, sorting, chips for video
 * and audio, pages), and on the right the preview player over the basket and the asset's details. The catalogue
 * holds the playout's clips, the stories' imports, the station's music and the stories' voice-over takes
 * (apps/api/src/mam.ts); the model is Nebula's asset browser (docs/DECISIONS.md, ADR 063).
 */
interface CatalogueDto {
  assets: MamAsset[];
  total: number;
  counts: MamCounts;
  page: number;
  size: number;
  pending: Array<{ storyId: string; title: string; code: string }>;
}
interface DetailDto {
  asset: MamAsset;
  url: string;
  thumb: string | null;
  uses: Array<{ storyId: string; title: string; via: 'storyboard' | 'story' | 'row' | 'owner' }>;
  edits: Array<{ id: string; title: string; storyId: string; storyTitle: string; updatedAt: string }>;
  meta: { description: string; tags: string[]; subclips: MamSubclip[]; updatedBy: string | null; updatedAt: string | null };
  history: Array<{ at: string; action: string; entityType: string; entityId: string | null; by: string | null }>;
  canEdit: boolean;
}
type ColKey = 'type' | 'status' | 'preview' | 'title' | 'description' | 'duration' | 'kind' | 'inserted' | 'origin' | 'size' | 'tags' | 'online' | 'used' | 'edits' | 'subclips';
type View = 'list' | 'grid';
type Tab = 'basket' | 'details';
interface Prefs {
  view: View;
  size: number;
  hidden: ColKey[];
  tab: Tab;
}

const COLS: Array<{ key: ColKey; label: Key; sort?: MamSort; className: string; fixed?: boolean }> = [
  { key: 'type', label: 'mamColType', sort: 'kind', className: 'c-type' },
  { key: 'status', label: 'mamColStatus', sort: 'status', className: 'c-status' },
  { key: 'preview', label: 'mamColPreview', className: 'c-preview' },
  { key: 'title', label: 'mamColName', sort: 'title', className: 'c-title', fixed: true },
  { key: 'description', label: 'mamColDescription', className: 'c-desc' },
  { key: 'duration', label: 'mamColDuration', sort: 'duration', className: 'c-dur' },
  { key: 'kind', label: 'mamColKind', sort: 'kind', className: 'c-kind' },
  { key: 'inserted', label: 'mamColInserted', sort: 'inserted', className: 'c-ins' },
  { key: 'origin', label: 'mamColOrigin', sort: 'file', className: 'c-origin' },
  { key: 'size', label: 'mamColSize', sort: 'size', className: 'c-size' },
  { key: 'tags', label: 'mamColTags', className: 'c-tags' },
  { key: 'online', label: 'mamColOnline', className: 'c-check' },
  { key: 'used', label: 'mamColUsed', className: 'c-check' },
  { key: 'edits', label: 'mamColEdits', className: 'c-check' },
  { key: 'subclips', label: 'mamColSubclips', className: 'c-check' },
];
const DEFAULT_PREFS: Prefs = { view: 'list', size: 100, hidden: ['size', 'tags', 'subclips'], tab: 'basket' };
const PREFS_KEY = 'airpost.mam.v2';
const SECTIONS: Array<{ key: MamSection; label: Key; icon: string; group: 'lib' | 'state' }> = [
  { key: 'all', label: 'mamSecAll', icon: '▤', group: 'lib' },
  { key: 'clip', label: 'mamSecClip', icon: '▶', group: 'lib' },
  { key: 'upload', label: 'mamSecUpload', icon: '⤒', group: 'lib' },
  { key: 'music', label: 'mamSecMusic', icon: '♪', group: 'lib' },
  { key: 'voiceover', label: 'mamSecVoiceover', icon: '🎙', group: 'lib' },
  { key: 'used', label: 'mamSecUsed', icon: '●', group: 'state' },
  { key: 'unused', label: 'mamSecUnused', icon: '○', group: 'state' },
  { key: 'new', label: 'mamSecNew', icon: '✦', group: 'state' },
];
const KIND_LABEL: Record<MamKind, Key> = { clip: 'mamKindClip', upload: 'mamKindUpload', music: 'mamKindMusic', voiceover: 'mamKindVoiceover' };
const VIA_LABEL: Record<DetailDto['uses'][number]['via'], Key> = { storyboard: 'mamViaStoryboard', story: 'mamViaStory', row: 'mamViaRow', owner: 'mamViaOwner' };
const DRAG_KEYS = 'application/x-airpost-mam-keys';
const DRAG_ITEM = 'application/x-airpost-mam-basket';

function loadPrefs(): Prefs {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      view: p.view === 'grid' ? 'grid' : 'list',
      size: (MAM_PAGE_SIZES as readonly number[]).includes(p.size ?? 0) ? p.size! : DEFAULT_PREFS.size,
      hidden: Array.isArray(p.hidden) ? p.hidden.filter((k): k is ColKey => COLS.some((c) => c.key === k && !c.fixed)) : DEFAULT_PREFS.hidden,
      tab: p.tab === 'details' ? 'details' : 'basket',
    };
  } catch {
    return DEFAULT_PREFS;
  }
}
function savePrefs(p: Prefs): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* a private window: the choices last for the page */
  }
}

const sizeOf = (bytes: number | null) => (bytes === null ? '—' : bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(2)} GB` : bytes >= 1024 ** 2 ? `${(bytes / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const tcOf = (ms: number | null) => (ms === null ? '—' : frameTc(ms));
const errText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const isTyping = (el: EventTarget | null) => el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
const downloadName = (a: MamAsset) => a.filename.replace(/[\\/:*?"<>|]+/g, '_') || a.key;
const mediaUrl = (a: MamAsset) => (a.kind === 'clip' ? `/api/media/${encodeURIComponent(a.id)}/proxy` : a.kind === 'upload' ? `/api/uploads/${encodeURIComponent(a.id)}/file` : a.kind === 'music' ? `/api/music/${encodeURIComponent(a.id)}/audio` : `/api/stories/${a.storyId}/voiceovers/${encodeURIComponent(a.id)}/audio`);

export function VideoHubPage() {
  const { can } = useSession();
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const setPrefs = (p: Partial<Prefs>) =>
    setPrefsState((prev) => {
      const next = { ...prev, ...p };
      savePrefs(next);
      return next;
    });
  // ── the query ──
  const [section, setSection] = useState<MamSection>('all');
  /** The middle shows the basket (the views' Καλάθι) instead of the catalogue; the side then shows the details. */
  const [inBasket, setInBasket] = useState(false);
  const [media, setMedia] = useState<MamMedia[]>([]);
  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<MamSort>('inserted');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState<CatalogueDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'err' | 'info'; text: string } | null>(null);
  // ── the selection: the focused row (blue, in the player) and the ticked rows (orange) ──
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const anchor = useRef<string | null>(null);
  const [detail, setDetail] = useState<DetailDto | null>(null);
  const [waves, setWaves] = useState<Record<string, string>>({});
  const [marks, setMarksState] = useState<{ key: string | null; inMs: number | null; outMs: number | null; item: string | null }>({ key: null, inMs: null, outMs: null, item: null });
  const [basket, setBasket] = useState<MamBasketItem[]>([]);
  const [colsOpen, setColsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const player = useRef<MamPlayerHandle>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (notice?.kind !== 'info') return;
    const h = window.setTimeout(() => setNotice(null), 3500);
    return () => window.clearTimeout(h);
  }, [notice]);

  // the search waits for the typing to pause
  useEffect(() => {
    const h = window.setTimeout(() => {
      setQ(qText.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(h);
  }, [qText]);

  useEffect(() => {
    let on = true;
    const params = new URLSearchParams({ section, sort, dir, page: String(page), size: String(prefs.size) });
    if (media.length === 1) params.set('media', media[0]!);
    if (q) params.set('q', q);
    const h = window.setTimeout(() => {
      setLoading(true);
      get<CatalogueDto>(`/api/mam/assets?${params.toString()}`)
        .then((r) => {
          if (!on) return;
          setData(r);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (!on) return;
          setLoading(false);
          setNotice({ kind: 'err', text: errText(e) });
        });
    }, 0);
    return () => {
      on = false;
      window.clearTimeout(h);
    };
  }, [section, media, q, sort, dir, page, prefs.size, reload]);

  const loadBasket = useCallback(async () => {
    try {
      setBasket((await get<{ items: MamBasketItem[] }>('/api/mam/basket')).items);
    } catch (e) {
      setNotice({ kind: 'err', text: errText(e) });
    }
  }, []);
  useEffect(() => {
    const h = window.setTimeout(() => void loadBasket(), 0);
    return () => window.clearTimeout(h);
  }, [loadBasket]);

  // the focused asset's details (and a sound's waveform, for the player's screen)
  const focusRef = useRef<string | null>(null);
  const waveAsked = useRef(new Set<string>());
  const loadDetail = useCallback(async (key: string) => {
    try {
      const d = await get<DetailDto>(`/api/mam/assets/${encodeURIComponent(key)}`);
      // an answer for an asset no longer focused is dropped
      if (focusRef.current !== key) return;
      setDetail(d);
      // a sound's waveform is drawn once per page
      if (d.asset.media === 'audio' && !waveAsked.current.has(key)) {
        waveAsked.current.add(key);
        analyseSound(d.url, 'rgba(120, 220, 140, 0.9)', 900)
          .then((w) => setWaves((prev) => ({ ...prev, [key]: w.wave })))
          .catch(() => waveAsked.current.delete(key));
      }
    } catch (e) {
      setNotice({ kind: 'err', text: errText(e) });
    }
  }, []);
  useEffect(() => {
    focusRef.current = focusKey;
    if (!focusKey) return;
    const h = window.setTimeout(() => void loadDetail(focusKey), 0);
    return () => window.clearTimeout(h);
  }, [focusKey, loadDetail]);

  const assets = useMemo(() => data?.assets ?? [], [data]);
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / prefs.size));
  const counts = data?.counts;
  const focusedDetail = detail && detail.asset.key === focusKey ? detail : null;
  const focused = assets.find((a) => a.key === focusKey) ?? focusedDetail?.asset ?? null;
  const curMarks: PlayerMarks = marks.key === focusKey ? { inMs: marks.inMs, outMs: marks.outMs } : { inMs: null, outMs: null };
  const setMarks = (m: PlayerMarks) => setMarksState((prev) => ({ key: focusKey, inMs: m.inMs, outMs: m.outMs, item: prev.key === focusKey ? prev.item : null }));
  const source: PlayerSource | null = focusedDetail
    ? { key: focusedDetail.asset.key, url: focusedDetail.url, media: focusedDetail.asset.media, title: focusedDetail.asset.title, durationMs: focusedDetail.asset.durationMs, poster: focusedDetail.thumb, wave: waves[focusedDetail.asset.key] ?? null }
    : null;
  const hidden = new Set(prefs.hidden);
  const cols = COLS.filter((c) => !hidden.has(c.key));

  const focus = (key: string, opts: { marks?: PlayerMarks; item?: string | null } = {}) => {
    setFocusKey(key);
    if (opts.marks) setMarksState({ key, inMs: opts.marks.inMs, outMs: opts.marks.outMs, item: opts.item ?? null });
    else if (marks.key !== key) setMarksState({ key, inMs: null, outMs: null, item: null });
  };
  const goSection = (s: MamSection) => {
    setSection(s);
    setInBasket(false);
    setPage(1);
    setChecked(new Set());
  };
  const toggleMedia = (m: MamMedia) => {
    setMedia((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [m]));
    setPage(1);
  };
  const sortBy = (s: MamSort) => {
    if (sort === s) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(s);
      setDir(s === 'inserted' || s === 'duration' || s === 'size' ? 'desc' : 'asc');
    }
    setPage(1);
  };
  const toggleCheck = (key: string) =>
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const onRowClick = (e: React.MouseEvent, a: MamAsset) => {
    if (e.shiftKey && anchor.current) {
      const i = assets.findIndex((x) => x.key === anchor.current);
      const j = assets.findIndex((x) => x.key === a.key);
      if (i >= 0 && j >= 0) {
        const [from, to] = i < j ? [i, j] : [j, i];
        setChecked((cur) => new Set([...cur, ...assets.slice(from, to + 1).map((x) => x.key)]));
      }
    } else if (e.ctrlKey || e.metaKey) toggleCheck(a.key);
    anchor.current = a.key;
    focus(a.key);
  };
  const allChecked = assets.length > 0 && assets.every((a) => checked.has(a.key));

  // ── the basket ──
  const basketCall = async (fn: () => Promise<{ items: MamBasketItem[] }>, ok?: string) => {
    try {
      setBasket((await fn()).items);
      if (ok) setNotice({ kind: 'info', text: ok });
    } catch (e) {
      setNotice({ kind: 'err', text: errText(e) });
    }
  };
  const addToBasket = (items: Array<{ assetKey: string; inMs?: number | null; outMs?: number | null; label?: string }>) => {
    if (!items.length) return;
    setPrefs({ tab: 'basket' });
    void basketCall(() => post('/api/mam/basket', { items }), `${items.length} ${t('mamAddedToBasket')}`);
  };
  const addSelection = () => {
    const keys = checked.size ? assets.filter((a) => checked.has(a.key)).map((a) => a.key) : focusKey ? [focusKey] : [];
    addToBasket(keys.map((k) => (k === focusKey && (curMarks.inMs !== null || curMarks.outMs !== null) ? { assetKey: k, inMs: curMarks.inMs, outMs: curMarks.outMs } : { assetKey: k })));
  };
  const addRange = () => focusKey && addToBasket([{ assetKey: focusKey, inMs: curMarks.inMs, outMs: curMarks.outMs }]);
  /** A basket item into the player, with its IN/OUT. */
  const loadItem = (i: MamBasketItem) => i.asset && focus(i.assetKey, { marks: { inMs: i.inMs, outMs: i.outMs }, item: i.id });
  const updateItemMarks = () => {
    if (!marks.item) return;
    void basketCall(() => put(`/api/mam/basket/${marks.item}`, { inMs: curMarks.inMs, outMs: curMarks.outMs }), t('mamItemUpdated'));
  };

  // ── the newsroom's metadata of the focused asset ──
  const saveMeta = async (meta: DetailDto['meta']) => {
    if (!focusedDetail) return false;
    try {
      const r = await put<{ meta: DetailDto['meta'] }>(`/api/mam/assets/${encodeURIComponent(focusedDetail.asset.key)}/meta`, { description: meta.description, tags: meta.tags, subclips: meta.subclips });
      setDetail({ ...focusedDetail, meta: { ...r.meta, updatedBy: t('mamYou'), updatedAt: r.meta.updatedAt } });
      setReload((n) => n + 1);
      setNotice({ kind: 'info', text: t('mamMetaSaved') });
      return true;
    } catch (e) {
      setNotice({ kind: 'err', text: errText(e) });
      return false;
    }
  };
  /** The marks as a subclip of the asset, named after its timecodes until someone names it in the details. */
  const saveSubclip = () => {
    if (!focusedDetail || curMarks.inMs === null || curMarks.outMs === null) return;
    const sub: MamSubclip = { id: `sc${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name: '', inMs: curMarks.inMs, outMs: curMarks.outMs };
    void saveMeta({ ...focusedDetail.meta, subclips: [...focusedDetail.meta.subclips, sub] }).then((ok) => ok && setPrefs({ tab: 'details' }));
  };

  // ── Μοντάζ: the chosen video into the editor ──
  /** The video the toolbar's Μοντάζ acts on: the one in the player, else the only one ticked. */
  const ticked = checked.size === 1 ? (assets.find((a) => checked.has(a.key)) ?? null) : null;
  const editTarget = focused?.media === 'video' ? focused : ticked?.media === 'video' ? ticked : null;
  /** Its edit in a story of the user's, a new one on the marks of the player, or a story made for it (the API decides). */
  const openEditor = async (key: string, storyId: string | null = null) => {
    if (opening) return;
    const m = key === focusKey ? curMarks : { inMs: null, outMs: null };
    setOpening(true);
    setNotice({ kind: 'info', text: t('mamEditOpening') });
    try {
      const r = await post<{ editId: string }>(`/api/mam/assets/${encodeURIComponent(key)}/edit`, { ...(storyId ? { storyId } : {}), inMs: m.inMs, outMs: m.outMs });
      navigate(`/edit/${r.editId}`);
    } catch (e) {
      setNotice({ kind: 'err', text: errText(e) });
      setOpening(false);
    }
  };

  // ── keys: the list moves with ↑ ↓, the player answers its own (space, J K L, I O, ← →, Home, End) ──
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isTyping(e.target) || sending) return;
    const idx = assets.findIndex((a) => a.key === focusKey);
    // the basket in the middle: ↑ ↓ load its items one by one, B puts the player's range in it, ticking is the catalogue's
    if (inBasket && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      const next = stepBasket(basket, marks.key === focusKey ? marks.item : null, e.key === 'ArrowDown' ? 1 : -1);
      if (next) {
        loadItem(next);
        listRef.current?.querySelector(`[data-item="${CSS.escape(next.id)}"]`)?.scrollIntoView({ block: 'nearest' });
      }
      e.preventDefault();
      return;
    }
    if (inBasket && !e.ctrlKey && !e.metaKey && (e.key === 'b' || e.key === 'B' || e.key === 'β' || e.key === 'Insert')) {
      addRange();
      e.preventDefault();
      return;
    }
    if (inBasket && (e.key === 'x' || e.key === 'χ' || ((e.key === 'a' || e.key === 'α') && (e.ctrlKey || e.metaKey)))) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const step = prefs.view === 'grid' && listRef.current ? Math.max(1, Math.round(listRef.current.clientWidth / 190)) : 1;
      const next = assets[Math.min(assets.length - 1, Math.max(0, idx < 0 ? 0 : idx + (e.key === 'ArrowDown' ? step : -step)))];
      if (next) {
        if (e.shiftKey) setChecked((cur) => new Set([...cur, next.key, ...(focusKey ? [focusKey] : [])]));
        focus(next.key);
        anchor.current = next.key;
        listRef.current?.querySelector(`[data-key="${CSS.escape(next.key)}"]`)?.scrollIntoView({ block: 'nearest' });
      }
      e.preventDefault();
      return;
    }
    if ((e.key === 'a' || e.key === 'α') && (e.ctrlKey || e.metaKey)) {
      setChecked(new Set(assets.map((a) => a.key)));
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      setChecked(new Set());
      return;
    }
    if (!e.ctrlKey && !e.metaKey && (e.key === 'x' || e.key === 'χ') && focusKey) {
      toggleCheck(focusKey);
      return;
    }
    if (!e.ctrlKey && !e.metaKey && (e.key === 'b' || e.key === 'B' || e.key === 'β' || e.key === 'Insert')) {
      addSelection();
      e.preventDefault();
      return;
    }
    if (!e.ctrlKey && !e.metaKey && !e.altKey && (e.key === 'e' || e.key === 'E' || e.key === 'ε' || e.key === 'Ε')) {
      if (editTarget && can('story.edit.own')) void openEditor(editTarget.key);
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && focusKey) {
      player.current?.playRange(curMarks.inMs, curMarks.outMs);
      e.preventDefault();
      return;
    }
    if (!e.ctrlKey && !e.metaKey) player.current?.handleKey(e);
  };

  const basketTotal = basket.reduce((sum, i) => sum + (i.asset ? (rangeLengthMs(i.inMs, i.outMs, i.asset.durationMs) ?? 0) : 0), 0);
  const from = total ? (page - 1) * prefs.size + 1 : 0;
  const to = Math.min(total, page * prefs.size);
  const canEditStories = can('story.edit.own');
  const sideTab: Tab = inBasket ? 'details' : prefs.tab;
  // in the middle or at the side, one at a time
  const basketPanel = (
    <BasketPanel
      items={basket}
      totalMs={basketTotal}
      loadedItem={marks.item}
      onLoad={loadItem}
      onDropKeys={(keys) => addToBasket(keys.map((k) => ({ assetKey: k })))}
      onRemove={(ids) => void basketCall(() => (ids.length === 1 ? del(`/api/mam/basket/${ids[0]}`) : del(`/api/mam/basket?ids=${ids.join(',')}`)))}
      onClear={() => void basketCall(() => del('/api/mam/basket'))}
      onOrder={(ids) => void basketCall(() => put('/api/mam/basket/order', { ids }))}
      onSend={() => setSending(true)}
      canSend={canEditStories}
    />
  );

  return (
    <div className="mam mm" tabIndex={-1} onKeyDown={onKeyDown}>
      {/* ── the views ── */}
      <nav className="mm-nav">
        <div className="mm-nav-head">{t('mamNavLibrary')}</div>
        {SECTIONS.filter((s) => s.group === 'lib').map((s) => (
          <button key={s.key} type="button" className={`mm-nav-item${section === s.key && !inBasket ? ' on' : ''}`} onClick={() => goSection(s.key)}>
            <span className="mm-nav-icon">{s.icon}</span>
            <span className="mm-nav-label">{t(s.label)}</span>
            <span className="mm-nav-count">{counts ? counts[s.key] : ''}</span>
          </button>
        ))}
        <div className="mm-nav-head">{t('mamNavState')}</div>
        {SECTIONS.filter((s) => s.group === 'state').map((s) => (
          <button key={s.key} type="button" className={`mm-nav-item${section === s.key && !inBasket ? ' on' : ''}`} onClick={() => goSection(s.key)}>
            <span className="mm-nav-icon">{s.icon}</span>
            <span className="mm-nav-label">{t(s.label)}</span>
            <span className="mm-nav-count">{counts ? counts[s.key] : ''}</span>
          </button>
        ))}
        <div className="mm-nav-head">{t('mamNavWork')}</div>
        <button type="button" className={`mm-nav-item${inBasket ? ' on' : ''}`} onClick={() => setInBasket(true)}>
          <span className="mm-nav-icon">🧺</span>
          <span className="mm-nav-label">{t('mamBasket')}</span>
          <span className="mm-nav-count">{basket.length}</span>
        </button>
        {data && data.pending.length > 0 && (
          <>
            <div className="mm-nav-head">{t('mamPending')}</div>
            {data.pending.map((p) => (
              <Link key={p.storyId} to={`/story/${p.storyId}`} className="mm-nav-item pending" title={p.title}>
                <span className="mm-nav-icon">⌛</span>
                <span className="mm-nav-label">{p.code}</span>
              </Link>
            ))}
          </>
        )}
        <p className="mm-keys">{t('mamKeysHelp')}</p>
      </nav>

      {/* ── the assets ── */}
      <section className="mm-browser">
        <header className="mm-panel-head">
          <span className="mm-panel-title">▤ {t('mamAssets')}</span>
          <span className="mm-panel-sub">{inBasket ? t('mamBasket') : t(SECTIONS.find((s) => s.key === section)!.label)}</span>
          {loading && !inBasket && <span className="mm-spin" aria-label="…" />}
        </header>
        {inBasket ? (
          <div className="mm-list" ref={listRef}>
            {basketPanel}
          </div>
        ) : (
          <>
            <div className="mm-toolbar">
              <div className="mm-search">
                <span className="mm-search-icon">⌕</span>
                <input type="search" value={qText} placeholder={t('mamSearchAll')} onChange={(e) => setQText(e.target.value)} />
              </div>
              <button type="button" className="mm-tool" onClick={() => setReload((n) => n + 1)} title={t('hubRefresh')}>
                ↻
              </button>
              <span className="mm-sep" />
              <button type="button" className={`mm-tool${prefs.view === 'list' ? ' on' : ''}`} onClick={() => setPrefs({ view: 'list' })} title={t('mamList')}>
                ☰
              </button>
              <button type="button" className={`mm-tool${prefs.view === 'grid' ? ' on' : ''}`} onClick={() => setPrefs({ view: 'grid' })} title={t('mamGrid')}>
                ▦
              </button>
              <div className="mm-cols">
                <button type="button" className={`mm-tool${colsOpen ? ' on' : ''}`} onClick={() => setColsOpen((x) => !x)} title={t('mamColumns')} disabled={prefs.view !== 'list'}>
                  ▥
                </button>
                {colsOpen && prefs.view === 'list' && (
                  <div className="mm-cols-menu" onMouseLeave={() => setColsOpen(false)}>
                    {COLS.filter((c) => !c.fixed).map((c) => (
                      <label key={c.key}>
                        <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => setPrefs({ hidden: hidden.has(c.key) ? prefs.hidden.filter((k) => k !== c.key) : [...prefs.hidden, c.key] })} />
                        {t(c.label)}
                      </label>
                    ))}
                    <button type="button" className="mm-link" onClick={() => setPrefs({ hidden: DEFAULT_PREFS.hidden })}>
                      {t('mamColumnsReset')}
                    </button>
                  </div>
                )}
              </div>
              <span className="mm-sep" />
              <button type="button" className="mm-tool accent" disabled={!checked.size && !focusKey} onClick={addSelection} title={`${t('mamAddToBasket')} (B)`}>
                ⊕ 🧺
              </button>
              {focused && (
                <a className="mm-tool" href={mediaUrl(focused)} download={downloadName(focused)} title={t('mamDownload')}>
                  ⤓
                </a>
              )}
              <button type="button" className="mm-tool accent" disabled={!editTarget || !canEditStories || opening} onClick={() => editTarget && void openEditor(editTarget.key)} title={`${t('mamEditHint')} (E)`}>
                🎬 {t('mamEdit')}
              </button>
              <span className="mm-toolbar-fill" />
              {checked.size > 0 && (
                <button type="button" className="mm-chip sel" onClick={() => setChecked(new Set())} title={t('mamClearSelection')}>
                  {checked.size} {t('mamSelected')} ✕
                </button>
              )}
            </div>
            <div className="mm-chips">
              {(['video', 'audio'] as MamMedia[]).map((m) => (
                <button key={m} type="button" className={`mm-chip media ${m}${media.includes(m) ? ' on' : ''}`} onClick={() => toggleMedia(m)}>
                  {media.includes(m) ? '✓ ' : ''}
                  {m === 'video' ? 'VIDEO' : 'AUDIO'}
                  <b>{counts ? counts[m] : ''}</b>
                </button>
              ))}
              {q && (
                <button type="button" className="mm-chip on" onClick={() => setQText('')}>
                  ⌕ {q} ✕
                </button>
              )}
            </div>

            <div className="mm-list" ref={listRef}>
              {(!data || (loading && assets.length === 0)) && <p className="mm-none">{t('loading')}</p>}
              {data && !loading && assets.length === 0 && <p className="mm-none">{q ? t('mamNoneSearch') : t('mamNoneHere')}</p>}
              {prefs.view === 'list' ? (
                <table className="mm-table">
                  <thead>
                    <tr>
                      <th className="c-check-box">
                        <input type="checkbox" checked={allChecked} onChange={() => setChecked(allChecked ? new Set() : new Set(assets.map((a) => a.key)))} title={t('mamCheckPage')} />
                      </th>
                      {cols.map((c) => (
                        <th key={c.key} className={`${c.className}${c.sort ? ' sortable' : ''}${c.sort === sort ? ' sorted' : ''}`} onClick={c.sort ? () => sortBy(c.sort!) : undefined}>
                          {t(c.label)}
                          {c.sort === sort && <span className="mm-sort">{dir === 'asc' ? '▲' : '▼'}</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((a) => (
                      <tr
                        key={a.key}
                        data-key={a.key}
                        className={`${focusKey === a.key ? 'focus' : ''}${checked.has(a.key) ? ' checked' : ''}`}
                        onClick={(e) => onRowClick(e, a)}
                        onDoubleClick={() => player.current?.playRange(null, null)}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData(DRAG_KEYS, JSON.stringify(checked.has(a.key) ? assets.filter((x) => checked.has(x.key)).map((x) => x.key) : [a.key]))}
                      >
                        <td className="c-check-box" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={checked.has(a.key)} onChange={() => toggleCheck(a.key)} />
                        </td>
                        {cols.map((c) => (
                          <td key={c.key} className={c.className}>
                            <Cell col={c.key} a={a} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="mm-grid">
                  {assets.map((a) => (
                    <div
                      key={a.key}
                      data-key={a.key}
                      className={`mm-card${focusKey === a.key ? ' focus' : ''}${checked.has(a.key) ? ' checked' : ''}`}
                      onClick={(e) => onRowClick(e, a)}
                      onDoubleClick={() => player.current?.playRange(null, null)}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData(DRAG_KEYS, JSON.stringify(checked.has(a.key) ? assets.filter((x) => checked.has(x.key)).map((x) => x.key) : [a.key]))}
                    >
                      <Thumb a={a} big />
                      <input type="checkbox" className="mm-card-check" checked={checked.has(a.key)} onClick={(e) => e.stopPropagation()} onChange={() => toggleCheck(a.key)} />
                      <span className="mm-card-title" title={a.title}>
                        {a.title}
                      </span>
                      <span className="mm-card-sub">
                        <span className={`mm-kind ${a.media}`}>{t(KIND_LABEL[a.kind])}</span> {a.uses > 0 && <span className="mm-used">●</span>} {fmtDateTime(a.insertedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <footer className="mm-pager">
              <label>
                {t('mamRows')}
                <select value={prefs.size} onChange={(e) => (setPrefs({ size: Number(e.target.value) }), setPage(1))}>
                  {MAM_PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <span className="mm-pager-range">
                {from}–{to} {t('mamOf')} {total}
              </span>
              <button type="button" className="mm-tool" disabled={page <= 1} onClick={() => setPage(1)} title={t('mamFirstPage')}>
                |‹
              </button>
              <button type="button" className="mm-tool" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} title={t('mamPrevPage')}>
                ‹
              </button>
              <span className="mm-pager-page">
                {page} / {pages}
              </span>
              <button type="button" className="mm-tool" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} title={t('mamNextPage')}>
                ›
              </button>
              <button type="button" className="mm-tool" disabled={page >= pages} onClick={() => setPage(pages)} title={t('mamLastPage')}>
                ›|
              </button>
            </footer>
          </>
        )}
      </section>

      {/* ── the player, the basket, the details ── */}
      <section className="mm-side">
        <div className="mm-player">
          <MamPlayer
            ref={player}
            source={source}
            marks={curMarks}
            onMarks={setMarks}
            subclips={focusedDetail?.meta.subclips ?? []}
            actions={
              <>
                {marks.item && marks.key === focusKey && (
                  <button type="button" className="mamp-btn wide" onClick={updateItemMarks} title={t('mamUpdateItemHint')}>
                    ⟳ {t('mamUpdateItem')}
                  </button>
                )}
                <button type="button" className="mamp-btn wide" disabled={!source} onClick={addRange} title={t('mamAddRangeHint')}>
                  ⊕ 🧺 [ ]
                </button>
                <button type="button" className="mamp-btn wide" disabled={!source || !focusedDetail?.canEdit || curMarks.inMs === null || curMarks.outMs === null} onClick={saveSubclip} title={t('mamSaveSubclipHint')}>
                  ✂ {t('mamSubclip')}
                </button>
                <button type="button" className="mamp-btn wide accent" disabled={!source || source.media !== 'video' || !canEditStories || opening} onClick={() => focusKey && void openEditor(focusKey)} title={`${t('mamEditRangeHint')} (E)`}>
                  🎬 {t('mamEdit')}
                </button>
              </>
            }
          />
        </div>
        <div className="mm-tabs">
          {!inBasket && (
            <button type="button" className={`mm-tab${sideTab === 'basket' ? ' on' : ''}`} onClick={() => setPrefs({ tab: 'basket' })}>
              🧺 {t('mamBasket')} <b>{basket.length}</b>
            </button>
          )}
          <button type="button" className={`mm-tab${sideTab === 'details' ? ' on' : ''}`} onClick={() => setPrefs({ tab: 'details' })}>
            ⓘ {t('mamDetails')}
          </button>
        </div>
        <div className="mm-tabbody">
          {sideTab === 'basket' ? (
            basketPanel
          ) : focusedDetail ? (
            <DetailsPanel
              // a save brings the stored values back (keywords tidied, subclips in order)
              key={`${focusedDetail.asset.key}|${focusedDetail.meta.updatedAt ?? ''}`}
              d={focusedDetail}
              canEditStories={canEditStories}
              canCreate={can('story.create')}
              opening={opening}
              onEdit={(storyId) => void openEditor(focusedDetail.asset.key, storyId)}
              onSave={saveMeta}
              onPlay={(s) => {
                setMarksState({ key: focusedDetail.asset.key, inMs: s.inMs, outMs: s.outMs, item: null });
                player.current?.playRange(s.inMs, s.outMs);
              }}
              onBasket={(s) => addToBasket([{ assetKey: focusedDetail.asset.key, inMs: s.inMs, outMs: s.outMs, label: s.name }])}
              onChanged={() => {
                setReload((n) => n + 1);
                void loadDetail(focusedDetail.asset.key);
              }}
              onNotice={(text, kind = 'info') => setNotice({ kind, text })}
            />
          ) : (
            <p className="mm-none">{t('mamPickAsset')}</p>
          )}
        </div>
      </section>

      {notice && (
        <div className={`mm-toast ${notice.kind}`} onClick={() => setNotice(null)}>
          {notice.text}
          <span className="mm-toast-x">✕</span>
        </div>
      )}
      {sending && <SendDialog items={basket} onClose={() => setSending(false)} onNotice={(text) => setNotice({ kind: 'err', text })} />}
    </div>
  );
}

/** A thumbnail: the playout's frame, a frame of the import, or a sound's mark. */
function Thumb({ a, big = false }: { a: MamAsset; big?: boolean }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`mm-thumb${big ? ' big' : ''}${a.media === 'audio' ? ' audio' : ''}`}>
      {a.kind === 'clip' && !broken && <img alt="" loading="lazy" src={`/api/media/${encodeURIComponent(a.id)}/thumb`} onError={() => setBroken(true)} />}
      {a.kind === 'upload' && <video muted preload="metadata" playsInline src={`/api/uploads/${encodeURIComponent(a.id)}/file#t=0.5`} />}
      {a.media === 'audio' && (
        <span className="mm-thumb-audio">
          <span className="mm-thumb-glyph">{a.kind === 'music' ? '♪' : '🎙'}</span>
          <span className="mm-thumb-bars">
            {Array.from({ length: 14 }, (_, i) => (
              <i key={i} style={{ height: `${25 + ((i * 37) % 70)}%` }} />
            ))}
          </span>
        </span>
      )}
      {a.durationMs !== null && <span className="mm-thumb-dur">{frameTc(a.durationMs).slice(3, 8)}</span>}
    </span>
  );
}

/** One cell of the list. */
function Cell({ col, a }: { col: ColKey; a: MamAsset }) {
  switch (col) {
    case 'type':
      return (
        <span className={`mm-type ${a.media}`} title={a.media === 'video' ? 'Video' : 'Audio'}>
          {a.media === 'video' ? '▶' : '♪'}
        </span>
      );
    case 'status':
      return (
        <span className="mm-status" title={a.status}>
          <span className={`mm-dot ${a.status === 'READY' ? 'ok' : 'warn'}`} />
          {a.status === 'READY' ? '' : a.status}
          {a.isNew && <span className="mm-new">{t('hubNewBadge')}</span>}
        </span>
      );
    case 'preview':
      return <Thumb a={a} />;
    case 'title':
      return (
        <span className="mm-title" title={a.title}>
          {a.title}
        </span>
      );
    case 'description':
      return (
        <span className="mm-desc" title={a.description}>
          {a.description}
        </span>
      );
    case 'duration':
      return <span className="mm-mono">{tcOf(a.durationMs)}</span>;
    case 'kind':
      return <span className={`mm-kind ${a.media}`}>{t(KIND_LABEL[a.kind])}</span>;
    case 'inserted':
      return <span className="mm-mono">{fmtDateTime(a.insertedAt)}</span>;
    case 'origin':
      return (
        <span className="mm-origin" title={a.storyTitle ? `${a.storyTitle} · ${a.filename}` : a.filename}>
          {a.kind === 'clip' ? (
            <span className="mm-mono">
              {a.libraryId !== null ? `playout/${a.libraryId}/` : 'playout/'}
              {a.filename}
            </span>
          ) : a.storyTitle ? (
            <>📰 {a.storyTitle}</>
          ) : (
            <span className="mm-mono">{t('mamStationMusic')}</span>
          )}
        </span>
      );
    case 'size':
      return <span className="mm-mono">{sizeOf(a.sizeBytes)}</span>;
    case 'tags':
      return (
        <span className="mm-tags">
          {a.tags.map((tag) => (
            <span key={tag} className="mm-tag">
              {tag}
            </span>
          ))}
        </span>
      );
    case 'online':
      return a.status === 'READY' ? <span className="mm-yes">✓</span> : <span className="mm-no">✕</span>;
    case 'used':
      return a.uses > 0 ? <span className="mm-yes" title={String(a.uses)}>✓{a.uses > 1 ? <sup>{a.uses}</sup> : null}</span> : null;
    case 'edits':
      return a.edits > 0 ? <span className="mm-yes" title={String(a.edits)}>✓{a.edits > 1 ? <sup>{a.edits}</sup> : null}</span> : null;
    case 'subclips':
      return a.subclips > 0 ? <span className="mm-yes">{a.subclips}</span> : null;
  }
}

/** The basket: ranges in order, their total, and the step into the editor. */
function BasketPanel(props: {
  items: MamBasketItem[];
  totalMs: number;
  loadedItem: string | null;
  onLoad(i: MamBasketItem): void;
  onDropKeys(keys: string[]): void;
  onRemove(ids: string[]): void;
  onClear(): void;
  onOrder(ids: string[]): void;
  onSend(): void;
  canSend: boolean;
}) {
  const { items } = props;
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [over, setOver] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    if (!confirmClear) return;
    const h = window.setTimeout(() => setConfirmClear(false), 3000);
    return () => window.clearTimeout(h);
  }, [confirmClear]);
  const tickedIds = items.filter((i) => ticked.has(i.id)).map((i) => i.id);
  const move = (dirn: -1 | 1) => {
    if (!tickedIds.length) return;
    const ids = items.map((i) => i.id);
    const order = dirn < 0 ? ids : [...ids].reverse();
    for (let k = 1; k < order.length; k++) {
      if (ticked.has(order[k]!) && !ticked.has(order[k - 1]!)) [order[k - 1], order[k]] = [order[k]!, order[k - 1]!];
    }
    props.onOrder(dirn < 0 ? order : order.reverse());
  };
  const onDrop = (e: React.DragEvent, beforeId: string | null) => {
    e.preventDefault();
    setOver(null);
    const moved = e.dataTransfer.getData(DRAG_ITEM);
    if (moved) {
      const ids = items.map((i) => i.id).filter((id) => id !== moved);
      const at = beforeId ? ids.indexOf(beforeId) : ids.length;
      ids.splice(at < 0 ? ids.length : at, 0, moved);
      props.onOrder(ids);
      return;
    }
    const keys = e.dataTransfer.getData(DRAG_KEYS);
    if (keys) props.onDropKeys(JSON.parse(keys) as string[]);
  };
  return (
    <div className="mm-basket" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, null)}>
      <div className="mm-basket-head">
        <span className="mm-basket-title">{t('mamBasket')}</span>
        <span className="mm-basket-total" title={t('mamBasketTotal')}>
          {frameTc(props.totalMs)}
        </span>
      </div>
      <div className="mm-basket-tools">
        <button type="button" className="mm-tool" disabled={!tickedIds.length} onClick={() => move(-1)} title={t('mamMoveUp')}>
          ▲
        </button>
        <button type="button" className="mm-tool" disabled={!tickedIds.length} onClick={() => move(1)} title={t('mamMoveDown')}>
          ▼
        </button>
        <button
          type="button"
          className="mm-tool"
          disabled={!tickedIds.length}
          onClick={() => {
            props.onRemove(tickedIds);
            setTicked(new Set());
          }}
          title={t('mamRemoveTicked')}
        >
          ✕
        </button>
        <button
          type="button"
          className={`mm-tool${confirmClear ? ' danger' : ''}`}
          disabled={!items.length}
          onClick={() => {
            if (!confirmClear) return setConfirmClear(true);
            setConfirmClear(false);
            props.onClear();
          }}
          title={t('mamClearBasket')}
        >
          🗑{confirmClear ? ` ${t('mamClearBasketConfirm')}` : ''}
        </button>
        <span className="mm-toolbar-fill" />
        <button type="button" className="mm-tool accent wide" disabled={!items.length || !props.canSend} onClick={props.onSend} title={t('mamToEditHint')}>
          ✂ {t('mamToEdit')}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="mm-none">{t('mamBasketEmpty')}</p>
      ) : (
        <table className="mm-table basket">
          <thead>
            <tr>
              <th className="c-check-box" />
              <th className="c-preview" />
              <th>{t('mamColName')}</th>
              <th className="c-kind">{t('mamColKind')}</th>
              <th className="c-dur">IN</th>
              <th className="c-dur">OUT</th>
              <th className="c-dur">{t('mamColDuration')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr
                key={i.id}
                data-item={i.id}
                className={`${props.loadedItem === i.id ? 'focus' : ''}${ticked.has(i.id) ? ' checked' : ''}${i.asset ? '' : ' gone'}${over === i.id ? ' drop' : ''}`}
                onClick={() => props.onLoad(i)}
                draggable
                onDragStart={(e) => e.dataTransfer.setData(DRAG_ITEM, i.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(i.id);
                }}
                onDragLeave={() => setOver((o) => (o === i.id ? null : o))}
                onDrop={(e) => {
                  e.stopPropagation();
                  onDrop(e, i.id);
                }}
              >
                <td className="c-check-box" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={ticked.has(i.id)}
                    onChange={() =>
                      setTicked((cur) => {
                        const next = new Set(cur);
                        if (next.has(i.id)) next.delete(i.id);
                        else next.add(i.id);
                        return next;
                      })
                    }
                  />
                </td>
                <td className="c-preview">{i.asset ? <Thumb a={i.asset} /> : <span className="mm-thumb" />}</td>
                <td>
                  <span className="mm-title" title={i.asset?.title ?? i.assetKey}>
                    {i.label || i.asset?.title || t('mamGone')}
                  </span>
                  {i.label && i.asset && <span className="mm-sub">{i.asset.title}</span>}
                </td>
                <td className="c-kind">{i.asset && <span className={`mm-kind ${i.asset.media}`}>{t(KIND_LABEL[i.asset.kind])}</span>}</td>
                <td className="c-dur mm-mono">{tcOf(i.inMs ?? (i.asset ? 0 : null))}</td>
                <td className="c-dur mm-mono">{tcOf(i.outMs ?? i.asset?.durationMs ?? null)}</td>
                <td className="c-dur mm-mono">{i.asset ? tcOf(rangeLengthMs(i.inMs, i.outMs, i.asset.durationMs)) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** A story the send dialog offers: the basket's own, or one found by its title. */
interface FoundStory {
  id: string;
  title: string;
  updatedAt?: string;
  placements?: Array<{ date: string; programmeName: string; order: number }>;
}

/** Where a story stands, to tell apart stories of the same title: its bulletin, date and place, else when it was last changed. */
function storyWhere(s: FoundStory): string {
  const p = s.placements?.[0];
  if (p) return `${p.programmeName} · ${fmtDateInput(p.date)} · #${p.order}${s.placements!.length > 1 ? ` +${s.placements!.length - 1}` : ''}`;
  return s.updatedAt ? fmtDateTime(s.updatedAt) : '';
}

/** The basket into a new edit: of a story found by its title, or of a new story. */
function SendDialog({ items, onClose, onNotice }: { items: MamBasketItem[]; onClose(): void; onNotice(text: string): void }) {
  const owners = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items) if (i.asset?.storyId) m.set(i.asset.storyId, i.asset.storyTitle ?? '');
    return [...m.entries()].map(([id, title]) => ({ id, title }));
  }, [items]);
  const firstVideo = items.find((i) => i.asset?.media === 'video')?.asset ?? null;
  const [mode, setMode] = useState<'new' | 'story'>(owners.length ? 'story' : 'new');
  const [title, setTitle] = useState(firstVideo?.title ?? '');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<FoundStory[]>([]);
  const [storyId, setStoryId] = useState<string | null>(owners[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ editId: string; dropped: Array<{ key: string; reason: string }> } | null>(null);
  useEffect(() => {
    const h = window.setTimeout(() => {
      if (!search.trim()) {
        setFound([]);
        return;
      }
      get<{ stories: FoundStory[] }>(`/api/stories/search?q=${encodeURIComponent(search.trim())}&size=10`)
        .then((r) => setFound(r.stories))
        .catch(() => setFound([]));
    }, 250);
    return () => window.clearTimeout(h);
  }, [search]);
  const choices: FoundStory[] = [...owners, ...found.filter((f) => !owners.some((o) => o.id === f.id))];
  const titleOf = (key: string) => items.find((i) => i.assetKey === key)?.asset?.title ?? key;
  const go = async () => {
    setBusy(true);
    try {
      const r = await post<{ editId: string; dropped: Array<{ key: string; reason: string }> }>('/api/mam/basket/edit', mode === 'new' ? { title: title.trim() || undefined } : { storyId });
      if (r.dropped.length) setResult(r);
      else navigate(`/edit/${r.editId}`);
    } catch (e) {
      onNotice(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mm-modal-back" onClick={onClose}>
      <div className="mm-modal" onClick={(e) => e.stopPropagation()}>
        <h3>✂ {t('mamToEdit')}</h3>
        {result ? (
          <>
            <p>{t('mamEditMade')}</p>
            <p className="mm-warn">{t('mamDroppedHint')}</p>
            <ul className="mm-dropped">
              {result.dropped.map((d) => (
                <li key={d.key}>
                  {titleOf(d.key)} — {d.reason === 'other-story' ? t('mamDroppedOther') : t('mamDroppedLength')}
                </li>
              ))}
            </ul>
            <div className="mm-modal-actions">
              <button type="button" className="mm-tool" onClick={onClose}>
                {t('close')}
              </button>
              <button type="button" className="mm-tool accent wide" onClick={() => navigate(`/edit/${result.editId}`)}>
                {t('mamOpenEdit')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mm-muted">{t('mamToEditHint')}</p>
            <label className="mm-radio">
              <input type="radio" checked={mode === 'story'} onChange={() => setMode('story')} /> {t('mamToStory')}
            </label>
            {mode === 'story' && (
              <div className="mm-modal-block">
                <input type="search" value={search} placeholder={t('hubStorySearch')} onChange={(e) => setSearch(e.target.value)} />
                <div className="mm-story-list">
                  {choices.length === 0 && <span className="mm-muted">{t('mamFindStory')}</span>}
                  {choices.map((c) => (
                    <label key={c.id} className={`mm-story${storyId === c.id ? ' on' : ''}`}>
                      <input type="radio" name="mam-story" checked={storyId === c.id} onChange={() => setStoryId(c.id)} />
                      <span className="mm-story-text">
                        <span className="mm-story-title">{c.title || '—'}</span>
                        <span className="mm-story-meta">
                          {storyWhere(c)}
                          {owners.some((o) => o.id === c.id) && <b> · {t('mamOwnsItems')}</b>}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <label className="mm-radio">
              <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> {t('mamToNewStory')}
            </label>
            {mode === 'new' && (
              <div className="mm-modal-block">
                <input value={title} maxLength={200} placeholder={t('mamNewStoryTitle')} onChange={(e) => setTitle(e.target.value)} />
              </div>
            )}
            <div className="mm-modal-actions">
              <button type="button" className="mm-tool" onClick={onClose}>
                {t('cancel')}
              </button>
              <button type="button" className="mm-tool accent wide" disabled={busy || (mode === 'story' && !storyId)} onClick={() => void go()}>
                {busy ? '…' : `✂ ${t('mamMakeEdit')}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The focused asset: the way into its story and editor, the newsroom's description, keywords and subclips, who uses it, its facts and history. */
function DetailsPanel(props: {
  d: DetailDto;
  canEditStories: boolean;
  canCreate: boolean;
  opening: boolean;
  /** Into the editor; the story picked below, or null to let the newsroom choose (the user's own, or a new one). */
  onEdit(storyId: string | null): void;
  onSave(meta: DetailDto['meta']): Promise<boolean>;
  onPlay(s: MamSubclip): void;
  onBasket(s: MamSubclip): void;
  onChanged(): void;
  onNotice(text: string, kind?: 'err' | 'info'): void;
}) {
  const { d } = props;
  const a = d.asset;
  const [desc, setDesc] = useState(d.meta.description);
  const [tags, setTags] = useState(d.meta.tags.join(', '));
  const [busy, setBusy] = useState<string | null>(null);
  const [storyId, setStoryId] = useState<string | null>(d.uses[0]?.storyId ?? null);
  // a story chosen in the list below is the one the editor opens in; untouched, the newsroom chooses
  const [picked, setPicked] = useState(false);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Array<{ id: string; title: string }>>([]);
  const [copied, setCopied] = useState(false);
  const dirty = desc !== d.meta.description || tags !== d.meta.tags.join(', ');
  useEffect(() => {
    if (a.kind !== 'clip') return;
    const h = window.setTimeout(() => {
      if (!search.trim()) {
        setFound([]);
        return;
      }
      get<{ stories: Array<{ id: string; title: string }> }>(`/api/stories/search?q=${encodeURIComponent(search.trim())}&size=10`)
        .then((r) => setFound(r.stories))
        .catch(() => setFound([]));
    }, 250);
    return () => window.clearTimeout(h);
  }, [search, a.kind]);
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try {
      await fn();
    } catch (e) {
      props.onNotice(errText(e), 'err');
    } finally {
      setBusy(null);
    }
  };
  const saveText = () => run('meta', async () => void (await props.onSave({ ...d.meta, description: desc, tags: tags.split(',').map((x) => x.trim()).filter(Boolean) })));
  const removeSub = (id: string) => run('sub', async () => void (await props.onSave({ ...d.meta, subclips: d.meta.subclips.filter((s) => s.id !== id) })));
  const renameSub = (id: string, name: string) => {
    const cur = d.meta.subclips.find((s) => s.id === id);
    if (!cur || cur.name === name.trim() || !name.trim()) return;
    void run('sub', async () => void (await props.onSave({ ...d.meta, subclips: d.meta.subclips.map((s) => (s.id === id ? { ...s, name: name.trim() } : s)) })));
  };
  const usesStory = !!storyId && d.uses.some((u) => u.storyId === storyId);
  const assign = () =>
    run('assign', async () => {
      if (!storyId) return;
      await put(`/api/stories/${storyId}/video`, { mode: 'clip', mediaAssetId: a.id });
      props.onNotice(t('hubAssigned'));
      props.onChanged();
    });
  const createStory = () =>
    run('create', async () => {
      const r = await post<{ storyId: string }>(`/api/video-hub/clips/${encodeURIComponent(a.id)}/story`, {});
      setStoryId(r.storyId);
      props.onNotice(t('hubNewStoryDone'));
      props.onChanged();
    });
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(a.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      props.onNotice(a.id);
    }
  };
  const facts: Array<[string, string]> = [
    [t('mamId'), a.id],
    [t('mamColKind'), t(KIND_LABEL[a.kind])],
    [t('mamFile'), a.filename],
    [t('mamDuration'), tcOf(a.durationMs)],
    [t('mamStatus'), a.status],
    [t('mamSize'), sizeOf(a.sizeBytes)],
    [t('mamColInserted'), fmtDateTime(a.insertedAt)],
    ...(a.libraryId !== null ? ([[t('mamLibrary'), String(a.libraryId)]] as Array<[string, string]>) : []),
    ...(a.storyTitle ? ([[t('hubStory'), a.storyTitle]] as Array<[string, string]>) : []),
  ];
  return (
    <div className="mm-details">
      <div className="mm-details-head">
        <span className={`mm-type ${a.media}`}>{a.media === 'video' ? '▶' : '♪'}</span>
        <h3 title={a.title}>{a.title}</h3>
      </div>
      <div className="mm-details-actions">
        {a.media === 'video' && (
          <button type="button" className="mm-tool accent wide" disabled={!props.canEditStories || props.opening} onClick={() => props.onEdit(a.kind === 'clip' && picked ? storyId : null)} title={t('mamEditHint')}>
            🎬 {t('hubOpenEditor')}
          </button>
        )}
        {a.storyId && (
          <Link className="mm-tool wide" to={`/story/${a.storyId}`}>
            📰 {t('mamOpenStory')}
          </Link>
        )}
        <a className="mm-tool wide" href={d.url} download={downloadName(a)}>
          ⤓ {t('mamDownload')}
        </a>
        <button type="button" className="mm-tool wide" onClick={() => void copyId()} title={a.id}>
          ⎘ {copied ? t('mamCopied') : t('mamCopyId')}
        </button>
      </div>

      {a.kind === 'clip' && (
        <div className="mm-block">
          <h4>{t('hubStory')}</h4>
          <div className="mm-row">
            <select
              value={storyId ?? ''}
              onChange={(e) => {
                setStoryId(e.target.value || null);
                setPicked(true);
              }}
            >
              <option value="">{t('hubStoryNone')}</option>
              {d.uses.map((u) => (
                <option key={u.storyId} value={u.storyId}>
                  {u.title}
                </option>
              ))}
              {found
                .filter((f) => !d.uses.some((u) => u.storyId === f.id))
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title}
                  </option>
                ))}
            </select>
          </div>
          <div className="mm-row">
            <input type="search" value={search} placeholder={t('hubStorySearch')} onChange={(e) => setSearch(e.target.value)} />
            {storyId && !usesStory && (
              <button type="button" className="mm-tool accent" disabled={!props.canEditStories || !!busy} onClick={() => void assign()}>
                {busy === 'assign' ? '…' : t('hubAssign')}
              </button>
            )}
          </div>
          {d.uses.length === 0 && (
            <div className="mm-row">
              <span className="mm-muted">{t('hubNoStoryHint')}</span>
              <button type="button" className="mm-tool accent" disabled={!props.canCreate || !!busy} onClick={() => void createStory()} title={t('hubNewStoryHint')}>
                {busy === 'create' ? '…' : `＋ ${t('hubNewStory')}`}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mm-block">
        <h4>{t('mamColDescription')}</h4>
        <textarea value={desc} rows={3} maxLength={4000} disabled={!d.canEdit} placeholder={d.canEdit ? t('mamDescPlaceholder') : ''} onChange={(e) => setDesc(e.target.value)} />
        <h4>{t('mamColTags')}</h4>
        <input value={tags} disabled={!d.canEdit} placeholder={d.canEdit ? t('mamTagsPlaceholder') : ''} onChange={(e) => setTags(e.target.value)} />
        <div className="mm-row end">
          {d.meta.updatedAt && (
            <span className="mm-muted">
              {d.meta.updatedBy ?? ''} · {fmtDateTime(d.meta.updatedAt)}
            </span>
          )}
          <button type="button" className="mm-tool accent" disabled={!d.canEdit || !dirty || busy === 'meta'} onClick={() => void saveText()}>
            {busy === 'meta' ? '…' : t('save')}
          </button>
        </div>
      </div>

      <div className="mm-block">
        <h4>
          {t('mamSubclips')} <span className="mm-muted">{d.meta.subclips.length || ''}</span>
        </h4>
        {d.meta.subclips.length === 0 ? (
          <p className="mm-muted">{t('mamNoSubclips')}</p>
        ) : (
          <table className="mm-table compact">
            <tbody>
              {d.meta.subclips.map((s) => (
                <tr key={s.id}>
                  <td>
                    {d.canEdit ? (
                      <input key={s.name} className="mm-subname" defaultValue={s.name} maxLength={120} title={t('mamSubclipName')} onBlur={(e) => renameSub(s.id, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
                    ) : (
                      <span className="mm-title">{s.name}</span>
                    )}
                  </td>
                  <td className="mm-mono">{frameTc(s.inMs)}</td>
                  <td className="mm-mono">{frameTc(s.outMs)}</td>
                  <td className="mm-mono">{frameTc(s.outMs - s.inMs)}</td>
                  <td className="mm-nowrap">
                    <button type="button" className="mm-tool" onClick={() => props.onPlay(s)} title={t('mamPlayRange')}>
                      ▶
                    </button>
                    <button type="button" className="mm-tool" onClick={() => props.onBasket(s)} title={t('mamAddToBasket')}>
                      ⊕
                    </button>
                    {d.canEdit && (
                      <button type="button" className="mm-tool" disabled={!!busy} onClick={() => void removeSub(s.id)} title={t('delete')}>
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mm-block">
        <h4>{t('mamUses')}</h4>
        {d.uses.length === 0 ? (
          <p className="mm-muted">{t('mamNoUses')}</p>
        ) : (
          <ul className="mm-uses">
            {d.uses.map((u) => (
              <li key={u.storyId}>
                <Link to={`/story/${u.storyId}`}>{u.title || '—'}</Link>
                <span className="mm-muted"> · {t(VIA_LABEL[u.via])}</span>
              </li>
            ))}
          </ul>
        )}
        <h4>{t('mamEdits')}</h4>
        {d.edits.length === 0 ? (
          <p className="mm-muted">{t('mamNoEdits')}</p>
        ) : (
          <ul className="mm-uses">
            {d.edits.map((e) => (
              <li key={e.id}>
                <Link to={`/edit/${e.id}`}>✂ {e.title || '—'}</Link>
                <span className="mm-muted">
                  {' '}
                  · {e.storyTitle} · {fmtDateTime(e.updatedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mm-block">
        <h4>{t('mamAsset')}</h4>
        <table className="mm-facts">
          <tbody>
            {facts.map(([k, v]) => (
              <tr key={k}>
                <th>{k}</th>
                <td className="mm-mono">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {d.history.length > 0 && (
        <div className="mm-block">
          <h4>{t('mamHistory')}</h4>
          <ul className="mm-history">
            {d.history.map((h, i) => (
              <li key={i}>
                <span className="mm-mono">{fmtDateTime(h.at)}</span> <b>{h.action}</b> <span className="mm-muted">{h.by ?? ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
